import { useCallback, useEffect, useState } from 'react'
import { Check, FileText, RefreshCw, ShieldCheck, X } from 'lucide-react'
import { Shell, useShellMeta } from './Shell.js'
import { Link } from '../router.js'
import { ToastProvider, useToast, apiGet, adminMutate, usePermissions } from './primitives.js'
import { useSaveBar } from '../shell/editing.js'
import { estErreurApi, fraicheurFr } from '../communication/lib.js'
import { useIntegrationCatalog, IntegrationStatusPill } from '../navigation/integration-status.js'

/**
 * FICHE D'UN SERVICE INTERNE LIVE CLUB (IA V2.1, §111-126).
 *
 * Main Courante et Parc ne sont plus des liens vers l'ancien écran
 * Services : ce sont des services métier connectés à Gestion par un
 * CONTRAT versionné. Cette fiche montre à quelle API Gestion est
 * connecté, si elle répond, comment elle s'authentifie, ce que Gestion
 * consomme, et si le service respecte le contrat — le mode d'emploi pour
 * le remplacer un jour sans toucher au métier.
 *
 * SANTÉ ≠ COMPATIBILITÉ (§135) : les deux sont affichées séparément.
 */

interface Exigence { id: string; label: string; ok: boolean; detail: string | null }
interface FicheService {
  key: string
  name: string
  contract: string
  base_url: string
  timeout_ms: number
  refresh_interval_s: number
  enabled: boolean
  token_configured: boolean
  last_success_at: string | null
  last_attempt_at: string | null
  last_error: string | null
  api: { status: string | null; version: string | null; generated_at: string | null } | null
  contract_check: {
    verdict: string; verdict_label: string; reachable: boolean
    required: Exigence[]; optional: Exigence[]
    api: { key: string | null; version: string | null }
    checked_at: string; duration_ms: number
  } | null
}

const CONSOMMATION: Record<string, { label: string; detail: string }[]> = {
  'main-courante': [
    { label: 'GET /dashboard', detail: 'headline (titre/sévérité), métriques, alertes actives, action principale — affichés sur le hub Sécurité, le Dashboard et l’état global.' },
    { label: 'service.status + generated_at + stale_after_seconds', detail: 'fraîcheur et disponibilité — pilotent DÉGRADÉ/périmé sans jamais afficher 0.' },
    { label: 'GET /capabilities · GET /permissions (optionnels)', detail: 'préparation SSO et catalogue de rôles.' },
    { label: 'Pont SQLite salles (ADAPTATEUR TEMPORAIRE, lecture seule)', detail: 'ouvertures/fermetures de salles par soirée + lien Shotgun — en attendant que le service expose ces données par API (cible optionnelle du contrat v1).' },
  ],
  'parc': [
    { label: 'GET /dashboard', detail: 'headline, métriques (retours, locations), alertes — affichés sur le hub Technique, le Dashboard et l’état global.' },
    { label: 'service.status + generated_at + stale_after_seconds', detail: 'fraîcheur et disponibilité.' },
    { label: 'GET /capabilities · GET /permissions (optionnels)', detail: 'préparation SSO et catalogue de rôles.' },
  ],
}

function Ligne({ label, children }: { label: string; children: React.ReactNode }) {
  return <li><span className="cm-actionlist__label">{label}</span><strong>{children}</strong></li>
}

function ExigenceLigne({ e }: { e: Exigence }) {
  return (
    <li className={`si-exigence ${e.ok ? 'si-exigence--ok' : 'si-exigence--ko'}`}>
      {e.ok ? <Check size={13} aria-hidden="true" /> : <X size={13} aria-hidden="true" />}
      <span>{e.label}{e.detail ? <em> — {e.detail}</em> : null}</span>
    </li>
  )
}

function Contenu({ serviceKey }: { serviceKey: string }) {
  const toast = useToast()
  const [data, setData] = useState<FicheService | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)
  const [url, setUrl] = useState('')
  const [jeton, setJeton] = useState('')
  const [verif, setVerif] = useState(false)
  const catalogue = useIntegrationCatalog()
  const canon = catalogue?.find((i) => (serviceKey === 'main-courante' ? i.id === 'main_courante' : i.id === 'parc')) ?? null
  const permissions = usePermissions()
  const gere = permissions.permissions.includes('integration.write')

  const charger = useCallback(() => {
    void apiGet<FicheService | { error: string; message?: string }>(`/api/v1/settings/integrations/services/${serviceKey}`)
      .then((j) => {
        if (estErreurApi(j)) { setErreur(j.message ?? 'Lecture impossible.'); return }
        setErreur(null); setData(j as FicheService); setUrl((j as FicheService).base_url)
      })
      .catch(() => setErreur('Gestion n’a pas répondu.'))
  }, [serviceKey])
  useEffect(() => { charger() }, [charger])

  const dirty = !!data && (url.trim() !== data.base_url || jeton.trim() !== '')
  useSaveBar({
    dirty: dirty && gere,
    saveLabel: 'Enregistrer la connexion',
    successMessage: 'Connexion mise à jour — lancez une vérification de conformité.',
    onSave: async () => {
      const body: Record<string, string> = {}
      if (data && url.trim() !== data.base_url) body['base_url'] = url.trim()
      if (jeton.trim()) body['token'] = jeton.trim()
      const r = await adminMutate<{ error?: string; message?: string }>(
        'PUT', `/api/v1/settings/integrations/services/${serviceKey}/config`, body)
      if (r.status !== 200) return r.data.message ?? 'Enregistrement refusé.'
      setJeton(''); charger()
      return true
    },
    onCancel: () => { if (data) setUrl(data.base_url); setJeton('') },
  })

  const lancerVerification = async () => {
    setVerif(true)
    await adminMutate('POST', `/api/v1/settings/integrations/services/${serviceKey}/check`, {})
    // Le worker réclame la demande en ≤ 10 s puis vérifie — on re-lit.
    for (let i = 0; i < 25; i++) {
      await new Promise((r) => setTimeout(r, 1200))
      const j = await apiGet<FicheService | { error: string }>(`/api/v1/settings/integrations/services/${serviceKey}`)
      if (!estErreurApi(j) && (j as FicheService).contract_check
        && (j as FicheService).contract_check!.checked_at !== data?.contract_check?.checked_at) {
        setData(j as FicheService)
        break
      }
    }
    setVerif(false)
    toast('ok', 'Vérification terminée.')
  }

  if (erreur) return <p className="lc-hint">{erreur}</p>
  if (!data) return null
  const check = data.contract_check

  return (
    <>
      <header className="cm-head">
        <div>
          <h1 className="lc-title">{data.name}</h1>
          <p className="lc-subtitle si-sous">
            <span className="lc-pill lc-pill--idle">Service interne</span>
            {canon ? <IntegrationStatusPill status={canon.status} label={canon.status_label} /> : null}
            {check ? (
              <span className={`lc-pill ${check.verdict === 'COMPATIBLE' ? 'lc-pill--ok' : check.verdict === 'NON_CONFIGURE' || check.verdict === 'INJOIGNABLE' ? 'lc-pill--idle' : 'lc-pill--warning'}`}>
                Contrat : {check.verdict_label}
              </span>
            ) : null}
          </p>
          <p className="lc-hint">
            « Toute application implémentant <strong>{data.contract}</strong> peut
            être connectée à Gestion à la place du service actuel — il suffira de
            changer l’URL et le jeton ci-dessous. »
          </p>
        </div>
      </header>

      <div className="acq-deuxcol">
        <section className="lc-panel acq-panel">
          <header className="lc-panel__head"><h2 className="lc-section-title">État</h2></header>
          <div className="lc-panel__body">
            <ul className="cm-actionlist">
              <Ligne label="Contrat">{data.contract}</Ligne>
              <Ligne label="Statut annoncé">{data.api?.status ?? 'jamais lu'}</Ligne>
              <Ligne label="Version du service">{data.api?.version ?? '—'}</Ligne>
              <Ligne label="Dernière lecture réussie">{data.last_success_at ? fraicheurFr(data.last_success_at) : 'jamais'}</Ligne>
              <Ligne label="Dernière tentative">{data.last_attempt_at ? fraicheurFr(data.last_attempt_at) : 'jamais'}</Ligne>
              <Ligne label="Cadence de lecture">toutes les {data.refresh_interval_s} s</Ligne>
            </ul>
            {data.last_error ? <p className="lc-hint acq-mismatch">Dernière erreur : {data.last_error}</p> : null}
          </div>
        </section>

        <section className="lc-panel acq-panel">
          <header className="lc-panel__head"><h2 className="lc-section-title">Connexion</h2>
            <span className="lc-hint">M2M — jeton au coffre, jamais réaffiché</span></header>
          <div className="lc-panel__body">
            {gere ? (
              <div className="acq-form">
                <label className="acq-champ">
                  <span>URL du service (réseau interne)</span>
                  <input className="lc-input" value={url} onChange={(e) => setUrl(e.target.value)}
                    placeholder="http://main-courante:3070/internal/gestion/v1" />
                </label>
                <label className="acq-champ">
                  <span>Jeton d’API</span>
                  <input className="lc-input" type="password" autoComplete="new-password" name={`svc-${serviceKey}`}
                    value={jeton} placeholder={data.token_configured ? '••••••••  (configuré — remplacer)' : 'Jeton du service'}
                    onChange={(e) => setJeton(e.target.value)} />
                </label>
                <p className="lc-hint"><ShieldCheck size={12} aria-hidden="true" /> Authentification : Bearer — distincte de toute identité utilisateur (jamais un jeton OIDC).</p>
              </div>
            ) : (
              <ul className="cm-actionlist">
                <Ligne label="URL">{data.base_url}</Ligne>
                <Ligne label="Jeton">{data.token_configured ? <span className="acq-ok"><Check size={13} aria-hidden="true" /> Configuré</span> : 'Absent'}</Ligne>
              </ul>
            )}
          </div>
        </section>
      </div>

      <section className="lc-panel acq-panel">
        <header className="lc-panel__head"><h2 className="lc-section-title">Gestion utilise</h2>
          <span className="lc-hint">ce qui est critique pour un service de remplacement</span></header>
        <div className="lc-panel__body">
          <ul className="si-conso">
            {(CONSOMMATION[serviceKey] ?? []).map((c) => (
              <li key={c.label}><strong>{c.label}</strong><span className="lc-hint"> — {c.detail}</span></li>
            ))}
          </ul>
        </div>
      </section>

      <section className="lc-panel acq-panel">
        <header className="lc-panel__head">
          <h2 className="lc-section-title">Diagnostic de conformité</h2>
          <button type="button" className="lc-btn" disabled={verif} onClick={() => void lancerVerification()}>
            <RefreshCw size={14} aria-hidden="true" className={verif ? 'cm-spin' : ''} />
            {verif ? 'Vérification…' : 'Vérifier la conformité'}
          </button>
        </header>
        <div className="lc-panel__body">
          {!check ? <p className="lc-hint">Jamais vérifiée — lancez une vérification (lecture seule, aucune mutation).</p> : (
            <>
              <p className="si-verdict">
                <strong>{check.verdict_label}</strong>
                {' '}— {check.required.filter((e) => e.ok).length}/{check.required.length} exigences obligatoires ·
                {' '}{check.optional.filter((e) => e.ok).length}/{check.optional.length} capacités optionnelles ·
                vérifié {fraicheurFr(check.checked_at)} ({check.duration_ms} ms)
              </p>
              <h3 className="lc-section-title si-h3">Obligatoire</h3>
              <ul className="si-exigences">{check.required.map((e) => <ExigenceLigne key={e.id} e={e} />)}</ul>
              <h3 className="lc-section-title si-h3">Optionnel</h3>
              <ul className="si-exigences">{check.optional.map((e) => <ExigenceLigne key={e.id} e={e} />)}</ul>
            </>
          )}
          <p className="lc-hint">
            <FileText size={12} aria-hidden="true" /> Cahier des charges complet :
            {' '}<code>docs/contracts/{serviceKey}-service-contract-v1.md</code> (dépôt Gestion).
          </p>
        </div>
      </section>
    </>
  )
}

export function ServiceIntegrationPage({ serviceKey }: { serviceKey: 'main-courante' | 'parc' }) {
  const meta = useShellMeta()
  return (
    <Shell active="settings" meta={meta}>
      <main className="lc-page lc-stagger ic-page">
        <Link className="lc-crumb" href="/settings/integrations">← Intégrations</Link>
        <ToastProvider><Contenu serviceKey={serviceKey} /></ToastProvider>
      </main>
    </Shell>
  )
}
